import React from 'react';
import Card from '@/components/ui/Card';
import { Workspace } from '@/types';
import RoleBadge from './RoleBadge';
import UserActionsMenu from './UserActionsMenu';
import { UserItem } from './types';

const UserCard: React.FC<{
  user: UserItem;
  editingId: string | null;
  resetPasswordId: string | null;
  workspaces: Workspace[];
  currentUserId?: string;
  onImpersonate: (user: UserItem) => void;
  onEdit: (user: UserItem) => void;
  onResetPassword: (user: UserItem) => void;
  onDelete: (user: UserItem) => void;
  renderEditForm: () => React.ReactNode;
  renderResetPasswordForm: () => React.ReactNode;
}> = ({
  user,
  editingId,
  resetPasswordId,
  workspaces,
  currentUserId,
  onImpersonate,
  onEdit,
  onResetPassword,
  onDelete,
  renderEditForm,
  renderResetPasswordForm,
}) => {
  const isEditing = editingId === user.id;
  const isResettingPassword = resetPasswordId === user.id;

  return (
    <Card key={user.id} padding="none">
      <div className="p-4">
        {/* User info header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{user.name}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
          </div>
          <RoleBadge role={user.role} />
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {user.workspaceIds && user.workspaceIds.length > 0
            ? user.workspaceIds.map((wid) => workspaces.find((w) => w.id === wid)?.shortName || '…').join(' · ')
            : '— no workspaces —'}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          Created {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
        </div>

        {/* Action buttons — only show when not editing or resetting */}
        {!isEditing && !isResettingPassword && (
          <div className="flex gap-2 border-t border-gray-100 dark:border-gray-800 pt-3">
            <UserActionsMenu
              user={user}
              currentUserId={currentUserId}
              onImpersonate={onImpersonate}
              onEdit={onEdit}
              onResetPassword={onResetPassword}
              onDelete={onDelete}
            />
          </div>
        )}

        {/* Inline edit form */}
        {isEditing && (
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
            {renderEditForm()}
          </div>
        )}

        {/* Inline password reset form */}
        {isResettingPassword && (
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
            {renderResetPasswordForm()}
          </div>
        )}
      </div>
    </Card>
  );
};

export default UserCard;
